"use client";
import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";

import type { AlertProps } from "./Alert";

interface AlertContextType {
  alert: AlertProps | null;
  showAlert: (value: AlertProps) => void;
  closeAlert: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);
export function useAlertContext(): AlertContextType | undefined {
  const context = useContext(AlertContext);
  if (!context) {
    console.warn("No alert context available for AlertProvided component");
  }
  return context;
}

export const AlertProvider: React.FC<{ children: ReactNode }> = ({
  children
}): JSX.Element => {
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [debounce, setDebounce] = useState<number | undefined>(undefined);

  const showAlert = (value: AlertProps) => {
    const timeoutValue = value.dismissible ? 10_000 : 7_000;
    setAlert(value);
    if (debounce) {
      window.clearTimeout(debounce);
      setDebounce(undefined);
    }
    if (value.variant !== "error") {
      setDebounce(window.setTimeout(closeAlert, timeoutValue));
    }
  };

  const closeAlert = () => {
    setAlert(null);
    if (debounce) {
      window.clearTimeout(debounce);
    }
    setDebounce(undefined);
  };
  return (
    <AlertContext.Provider value={{ alert, showAlert, closeAlert }}>
      {children}
    </AlertContext.Provider>
  );
};
